import React from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../helper/formatCurrency";

const OrderSummary = ({
  totalItemsAmount,
  gstAmount,
  gstRate,
  totalPackageCharge,
  totalWithGST,
  locale,
  form,
  setForm,
  phoneNumber,
  loggedIn,
  addressErrors,
  jwtUser,
  recentAddresses,
  isDineIn,
  isPlacingOrder,
  restaurantStatus,
  handlePlaceOrder,
  handleShare,
  joinToken,
  allocationStatus,
  handleUseCurrentLocation,
  isFetchingLocation,
  locationPermission,
  taxLabel = "GST",

}) => {
  const { t } = useTranslation();
  const WHATSAPP_REDIRECT =
    process.env.REACT_APP_WHATSAPP_REDIRECT === "true";

  return (
    <div
      className="bg-white rounded-2xl shadow-lg p-4 md:p-6 sticky"
      style={{ top: "8rem" }}
    >
      <h3 className="text-xl font-bold text-gray-800 mb-4">
        {t("orderSummary")}
      </h3>

      {/* Price Breakdown */}
      <div className="space-y-3 mb-4 pb-4 border-bottom border-brand-200">
        <div className="d-flex justify-content-between text-gray-700">
          <span>{t("itemTotal")}</span>
          <span className="font-semibold">
            {formatCurrency(totalItemsAmount, locale)}
          </span>
        </div>
        {!isDineIn && (
          <div className="d-flex justify-content-between text-gray-700">
            <span>
              <p>{taxLabel} ({gstRate * 100}%)</p>
            </span>
            <span className="font-semibold">
              {formatCurrency(gstAmount, locale)}
            </span>
          </div>
        )}
        {totalPackageCharge > 0 && (
          <div className="d-flex justify-content-between text-gray-700">
            <span>{t("packageCharge") || "Packaging Charges"}</span>
            <span className="font-semibold">
              {formatCurrency(totalPackageCharge, locale)}
            </span>
          </div>
        )}

      </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="font-bold mb-0 text-gray-800">{t("toPay")}</h5>
        <h4 className="font-bold mb-0 text-brand-600">
          {formatCurrency(totalWithGST, locale)}
        </h4>
      </div>

      {/* Delivery Form - Only for non-dine-in orders */}
      {!isDineIn && (
        <div className="space-y-3">
          <div>
            <label className="form-label text-sm font-semibold text-gray-700">
              {t("mobileNumber")}
            </label>
            <input
              type="tel"
              className="form-control rounded-lg border-2 border-brand-200 focus:border-brand-400"
              disabled={loggedIn}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (!loggedIn) {
                  setForm((prev) => ({
                    ...prev,
                    phoneNumber: value,
                  }));
                }
              }}
              value={loggedIn ? phoneNumber : form.phoneNumber || ""}
            // maxLength={10}
            />
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label text-sm font-semibold text-gray-700 mb-0">
                {t("houseFlat")}
              </label>

              {/* Use Current Location Button */}
              <button
                type="button"
                onClick={handleUseCurrentLocation}
                disabled={isFetchingLocation}
                className={`btn btn-sm ${locationPermission === "denied"
                  ? "btn-warning"
                  : "btn-outline-primary"
                  }`}
                style={{ fontSize: "0.75rem", padding: "4px 8px" }}
              >
                {isFetchingLocation
                  ? "Fetching..."
                  : locationPermission === "denied"
                    ? "Enable Location"
                    : "📍 Current Location"}
              </button>
            </div>

            <input
              type="text"
              className={`form-control rounded-lg border-2 ${addressErrors.houseNo ? "border-red-400" : "border-brand-200"
                } focus:border-brand-400`}
              placeholder={t("houseFlat")}
              value={form.houseNo}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  houseNo: e.target.value,
                }))
              }
            />

            {addressErrors.houseNo && (
              <div className="text-red-600 text-sm mt-1">
                {addressErrors.houseNo}
              </div>
            )}
          </div>


          <div>
            <label className="form-label text-sm font-semibold text-gray-700">
              {t("area")}
            </label>
            <input
              type="text"
              className={`form-control rounded-lg border-2 ${addressErrors.area ? "border-red-400" : "border-brand-200"
                } focus:border-brand-400`}
              placeholder={t("area")}
              value={form.area}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, area: e.target.value }))
              }
            />
            {addressErrors.area && (
              <div className="text-red-600 text-sm mt-1">
                {addressErrors.area}
              </div>
            )}
          </div>

          <div>
            <label className="form-label text-sm font-semibold text-gray-700">
              {t("landmarkOptional")}
            </label>
            <input
              type="text"
              className="form-control rounded-lg border-2 border-brand-200 focus:border-brand-400"
              placeholder={t("landmarkOptional")}
              value={form.landmark}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  landmark: e.target.value,
                }))
              }
            />
          </div>

          <div>
            <label className="form-label text-sm font-semibold text-gray-700">
              {t("pincode")}
            </label>
            <input
              type="text"
              className={`form-control rounded-lg border-2 ${addressErrors.pincode ? "border-red-400" : "border-brand-200"
                } focus:border-brand-400`}
              placeholder={t("pincode")}
              value={form.pincode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setForm((prev) => ({ ...prev, pincode: value }));
              }}
            // maxLength={6}
            />
            {addressErrors.pincode && (
              <div className="text-red-600 text-sm mt-1">
                {addressErrors.pincode}
              </div>
            )}
          </div>

          {/* Recent Addresses */}
          {jwtUser && recentAddresses.length > 0 && (
            <div>
              <label className="form-label text-sm font-semibold text-gray-700 mb-2">
                {t("recentAddresses")}:
              </label>
              <div className="space-y-2">
                {recentAddresses.slice(0, 3).map((addr) => (
                  <div
                    key={addr._id}
                    className="p-3 bg-brand-50 rounded-lg cursor-pointer hover:bg-brand-100 transition-colors"
                    onClick={() => {
                      const parts = addr.address
                        .split(",")
                        .map((p) => p.trim());
                      setForm((prev) => ({
                        ...prev,
                        houseNo: parts[0] || "",
                        area: parts[1] || "",
                        landmark: parts[2] || "",
                        pincode: addr.pincode || "",
                      }));
                    }}
                  >
                    <div className="text-sm text-gray-700">{addr.address}</div>
                    <div className="text-xs text-gray-500">{addr.pincode}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Terms and Conditions */}
          <div className="bg-brand-50 border-2 border-brand-200 rounded-xl p-4 mt-4">
            <h6 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-brand-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {t("message")}
            </h6>
            <ul
              className="text-sm text-gray-700 space-y-2"
              style={{ listStyle: "none", padding: 0 }}
            >
              <li className="flex items-start gap-2">
                <span className="text-brand-500 mt-0.5">•</span>
                <span>{t("term1")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-500 mt-0.5">•</span>
                <span>{t("term2")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-500 mt-0.5">•</span>
                <span>{t("term3")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-500 mt-0.5">•</span>
                <span>{t("term4")}</span>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Allocation Status Warning for Dine-in */}
      {isDineIn && allocationStatus === "pending" && (
        <div className="alert alert-warning mb-4 rounded-xl" role="alert">
          <div className="d-flex align-items-start">
            <i className="feather-clock me-2 mt-1"></i>
            <div>
              <strong>
                {t("waitingForApproval") || "Waiting for Approval"}
              </strong>
              <p className="mb-0 small mt-1">
                {t("tableRequestPending") ||
                  "Your table access request is pending. You can place orders once the restaurant accepts your request."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Place Order Button */}
      <button
        onClick={handlePlaceOrder}
        disabled={
          isPlacingOrder ||
          restaurantStatus === "offline" ||
          (isDineIn && allocationStatus !== "accepted")
        }
        className="btn btn-lg w-100 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl mt-4"
        style={{
          background:
            restaurantStatus === "offline" ||
              isPlacingOrder ||
              (isDineIn && allocationStatus !== "accepted")
              ? "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)"
              : "linear-gradient(135deg, rgb(var(--brand-500)) 0%, rgb(var(--brand-600)) 100%)",
          border: "none",
          padding: "0.875rem 2rem",
          fontSize: "1.1rem",
        }}
      >
        {isPlacingOrder
          ? t("processing")
          : restaurantStatus === "offline"
            ? t("orderingUnavailable")
            : isDineIn && allocationStatus !== "accepted"
              ? t("waitingForApproval") || "Waiting for Approval"
              : isDineIn
                ? t("placeOrder") || "Place Order"
                : WHATSAPP_REDIRECT
                  ? t("placeOrderWhatsapp") || "Place Order on WhatsApp"
                  : t("submit") || "Submit Order"}
      </button>
    </div>
  );
};

export default OrderSummary;
